  import React, { useEffect, useState, useMemo, useCallback } from 'react';
  import { 
    Box, 
    Typography, 
    TextField, 
    Pagination, 
    Grid, 
    Card, 
    CardContent, 
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent
  } from '@mui/material';
  import axios from 'axios';
  import AccessTimeIcon from '@mui/icons-material/AccessTime';
  import PersonIcon from '@mui/icons-material/Person';

  interface LogEntry {
    id: number;
    role: string;
    username: string;
    action: string;
    timestamp: string;
  }

  type SortType = 'dateDesc' | 'dateAsc' | 'userAsc' | 'userDesc' | 'roleAsc' | 'roleDesc' | 'actionAsc' | 'actionDesc';

  const LOGS_PER_PAGE = 100;

  const ConsoleLog: React.FC = () => {
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [page, setPage] = useState(1);
    const [sortType, setSortType] = useState<SortType>('dateDesc');

    const username = localStorage.getItem('username');
    const currentRole = localStorage.getItem('role');

    useEffect(() => {
      document.title = 'Логи | Zerde';
    }, []);

    const fetchLogs = useCallback(async () => {
      try {
        const response = await axios.get('https://tbl.zerdegroup.kz/api/logs');
        setLogs(response.data);
      } catch (error) {
        console.error('Ошибка при получении логов:', error);
      }
    }, []);

    const cleanupExpiredLogs = useCallback(async () => {
      try {
        await axios.delete('https://tbl.zerdegroup.kz/api/logs/cleanup');
        fetchLogs();
      } catch (error) {
        console.error('Ошибка при очистке логов:', error);
      }
    }, [fetchLogs]);

    useEffect(() => {
      cleanupExpiredLogs();
      
      fetchLogs();
      const interval = setInterval(fetchLogs, 30000);
      return () => clearInterval(interval);
    }, [fetchLogs, cleanupExpiredLogs]);

    const filteredLogs = useMemo(() => {
      let sortedLogs = logs.filter(log => {
        // фильтр для Galiya
        if (username === 'Galiya' && currentRole === 'admin') {
          if (!log.action.includes('| Редактировать отчет')) {
            return false;
          }
        }

        const logDate = new Date(log.timestamp);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        if (end) {
          end.setDate(end.getDate() + 1);
        }

        const isWithinDateRange =
          (!start || logDate >= start) &&
          (!end || logDate < end);

        const matchesSearchTerm = 
          log.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.action.toLowerCase().includes(searchTerm.toLowerCase());

        return isWithinDateRange && matchesSearchTerm;
      });

      // cортировка 
      switch (sortType) {
        case 'dateAsc':
          sortedLogs.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          break;
        case 'dateDesc':
          sortedLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          break;
        case 'userAsc':
          sortedLogs.sort((a, b) => a.username.localeCompare(b.username));
          break;
        case 'userDesc':
          sortedLogs.sort((a, b) => b.username.localeCompare(a.username));
          break;
        case 'roleAsc':
          sortedLogs.sort((a, b) => a.role.localeCompare(b.role));
          break;
        case 'roleDesc':
          sortedLogs.sort((a, b) => b.role.localeCompare(a.role));
          break;
        case 'actionAsc':
          sortedLogs.sort((a, b) => a.action.localeCompare(b.action));
          break;
        case 'actionDesc':
          sortedLogs.sort((a, b) => b.action.localeCompare(a.action));
          break;
      }

      return sortedLogs;
    }, [logs, searchTerm, startDate, endDate, username, currentRole, sortType]);

    const totalPages = Math.ceil(filteredLogs.length / LOGS_PER_PAGE);

    const paginatedLogs = useMemo(() => {
      const startIndex = (page - 1) * LOGS_PER_PAGE;
      return filteredLogs.slice(startIndex, startIndex + LOGS_PER_PAGE);
    }, [filteredLogs, page]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      setPage(1);
    };

    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setStartDate(e.target.value);
      setPage(1);
    };

    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setEndDate(e.target.value);
      setPage(1);
    };

    const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
    };

    const handleSortChange = (e: SelectChangeEvent<SortType>) => {
      setSortType(e.target.value as SortType);
      setPage(1);
    };

    return (
      <Box sx={{}}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs>
            <TextField
              label="Поиск"
              fullWidth
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
              size='small'
            />
          </Grid>
          <Grid item >
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <TextField
                label="Начальная дата"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={handleStartDateChange}
                size='small'
              />
              <TextField
                label="Конечная дата"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={handleEndDateChange}
                size='small'
              />
            </Box>
          </Grid>
          <Grid item >
            <FormControl variant="outlined">
              <InputLabel>Сортировка</InputLabel>
              <Select
                value={sortType}
                onChange={handleSortChange}
                label="Сортировка"
                sx={{width: '200px'}}
                size='small'
              >
                <MenuItem value="dateDesc">Дата (новые)</MenuItem>
                <MenuItem value="dateAsc">Дата (старые)</MenuItem>
                <MenuItem value="userAsc">Пользователь (A-Z)</MenuItem>
                <MenuItem value="userDesc">Пользователь (Z-A)</MenuItem>
                <MenuItem value="roleAsc">Роль (A-Z)</MenuItem>
                <MenuItem value="roleDesc">Роль (Z-A)</MenuItem>
                <MenuItem value="actionAsc">Действие (A-Z)</MenuItem>
                <MenuItem value="actionDesc">Действие (Z-A)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

  <Box sx={{maxHeight: '69vh', overflow: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mr: 1}}>
          {paginatedLogs.map((log) => (
            <Card 
              key={log.id} 
              variant="outlined"
              sx={{ 
                transition: 'all 0.2s ease',
                '&:hover': { 
                  boxShadow: 2,
                  transform: 'translateY(-2px)' 
                }
              }}
            >
              <CardContent>
                <Grid container spacing={1} alignItems="center">
                  <Grid item >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="primary" fontSize="small" />
                      <Typography variant="body2">
                        {log.username}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={log.role} 
                        size="small" 
                        color="secondary" 
                        variant="outlined" 
                      />
                    </Box>
                  </Grid>
                  <Grid item >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTimeIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        {new Date(log.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                    Пользователь {log.username} {log.action}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            color="primary"
            size="medium"
          />
        </Box>
      </Box>
    );
  };

  export default React.memo(ConsoleLog);